import { DEFAULT_INCLUSIONS, type Vehicle } from '../data/vehicles'

/* The buying assurances a car carries — what the card lists and what the
   detail page spells out in full. One derivation, two surfaces, so a card can
   never disagree with the page it links to.

   Two of them are facts about the car (bank lease, what the price folds in);
   the last two are house policy and true of every deal. */

export type Assurance = {
  /** Stable key — the labels are display copy and may be reworded. */
  key:
    | 'bankLeased'
    | 'insurance'
    | 'registration'
    | 'withholdingTax'
    | 'tracker'
    | 'noHiddenCharges'
    | 'deliveryOnSpot'
  label: string
  /** The card's reading. A card in a three-up grid is about 290px wide, and
      at that width "Registration included" takes a whole line to itself — so
      the pills there drop the word the heading above them already says. */
  short: string
  included: boolean
  /** Longer form, for the detail page. */
  detail: string
}

export function assurancesFor(vehicle: Vehicle): Assurance[] {
  const included = { ...DEFAULT_INCLUSIONS, ...vehicle.inclusions }

  return [
    {
      key: 'bankLeased',
      label: 'Bank leased',
      short: 'Bank leased',
      included: vehicle.financingAvailable,
      detail: vehicle.financingAvailable
        ? 'Available on lease through our partner banks.'
        : 'Cash purchase only — no bank will lease this unit.',
    },
    {
      key: 'insurance',
      label: 'Insurance included',
      short: 'Insurance',
      included: included.insurance,
      detail: included.insurance
        ? 'First-year comprehensive cover is built into the plan.'
        : 'Insurance is arranged by the buyer.',
    },
    {
      key: 'registration',
      label: 'Registration included',
      short: 'Registration',
      included: included.registration,
      detail: included.registration
        ? 'Number plate and excise registration handled by us.'
        : 'Registration is filed by the buyer.',
    },
    {
      key: 'withholdingTax',
      label: 'W.H tax included',
      short: 'W.H tax',
      included: included.withholdingTax,
      detail: included.withholdingTax
        ? 'Withholding tax is already covered in the quoted figure.'
        : 'Withholding tax is payable by the buyer.',
    },
    {
      key: 'tracker',
      label: 'Tracker included',
      short: 'Tracker',
      included: included.tracker,
      detail: included.tracker
        ? 'A vehicle tracker is covered in the quoted figure.'
        : 'A vehicle tracker is not included in the quoted figure.',
    },
    /* House policy: true on every car we sell, hence no data behind them. */
    {
      key: 'noHiddenCharges',
      label: 'No hidden charges',
      short: 'No hidden charges',
      included: true,
      detail: 'The price you see is the price you pay. Nothing added later.',
    },
    {
      key: 'deliveryOnSpot',
      label: 'Delivery on the spot',
      short: 'On-spot delivery',
      included: true,
      detail: 'Paperwork cleared the same day — drive it home from the floor.',
    },
  ]
}
